import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Avatar, Box, Button, CircularProgress, Chip, IconButton, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { normalizeAvatarUrl } from "../../../../utils/avatar";
import { UITableShell, UITableToolbar, UITableActionCell } from "../../../../components/ui";

export default function ArchivedProjects({ instanceUniqueId, onBack }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/archived-projects`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
        params: { instanceUniqueId },
      });
      setRows(response.data?.projects || []);
    } catch (e) {
      setError(e?.response?.data?.message || "Unable to load archived projects.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setPage(0); }, [search]);
  const visible = useMemo(() => rows.filter((row) =>
    [row.OrderId, row.ProjectName, row.CustomerName, row.id]
      .some((value) => String(value ?? "").toLowerCase().includes(search.trim().toLowerCase()))
  ), [rows, search]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(visible.length / rowsPerPage) - 1));
  const pagedRows = visible.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);
  const dash = "—";
  const cellSx = { px: 2, py: 1.5, fontSize: "0.875rem" };

  return (
    <Box sx={{
      p: { xs: 1, md: 2 }, display: "flex", flexDirection: "column",
      height: "calc(100vh - 60px)", overflow: "hidden", width: "100%",
    }}>
      <UITableShell customLayout paperSx={{
        flex: 1, minHeight: 0, borderRadius: "10px", border: "1px solid",
        borderColor: "divider", borderTop: "3px solid", borderTopColor: "primary.main",
        boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
      }}>
        <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography variant="h6" fontWeight={700}>Project Archive</Typography>
              <Chip label={`${visible.length} total`} size="small" sx={{
                backgroundColor: "var(--ec-status-neutral-bg)",
                color: "var(--ec-status-neutral-text)",
                border: "1px solid var(--ec-status-neutral-border)",
                fontWeight: 600, fontSize: "0.72rem", height: 22,
              }} />
              <Button size="small" variant="outlined" onClick={onBack} sx={{ textTransform: "none" }}>
                Back to Management
              </Button>
            </Stack>
            <Tooltip title="Refresh list">
              <span>
                <IconButton size="small" onClick={reload} disabled={loading} aria-label="Refresh archived projects">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Hidden from operational lists, but retained for internal testing. Open a project directly without restoring it.
          </Typography>
          <TextField
            size="small" fullWidth placeholder="Search archived projects"
            value={search} onChange={(event) => setSearch(event.target.value)}
            sx={{ mt: 1.5, maxWidth: 440 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
          />
        </UITableToolbar>

        {error && <Alert severity="error" sx={{ mx: 2, mt: 2 }}>{error}</Alert>}
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow>
                {["Project #", "Name", "Customer", "Archived", "Archived by", "Actions"].map((label) => (
                  <TableCell key={label} sx={{ px: 2, py: 1.5, fontWeight: 700 }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={24} />
                </TableCell></TableRow>
              ) : pagedRows.length ? pagedRows.map((row) => (
                <TableRow key={row.id} hover sx={{
                  backgroundColor: "background.paper",
                  "&:hover": { backgroundColor: "action.hover" },
                }}>
                  <TableCell sx={cellSx}>{row.OrderId ?? dash}</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{row.ProjectName || dash}</TableCell>
                  <TableCell sx={cellSx}>{row.CustomerName || dash}</TableCell>
                  <TableCell sx={cellSx}>{row.archivedAt ? new Date(row.archivedAt).toLocaleString() : dash}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.archivedByUser ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar src={normalizeAvatarUrl(row.archivedByUser.profilePicture) || undefined} sx={{ width: 30, height: 30, fontSize: 12 }}>
                          {String(row.archivedByUser.first_name || row.archivedByUser.last_name || "?").charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {[row.archivedByUser.first_name, row.archivedByUser.last_name].filter(Boolean).join(" ") || `User #${row.archivedByUserId}`}
                        </Typography>
                      </Stack>
                    ) : (row.archivedByUserId ? `User #${row.archivedByUserId} (unavailable)` : dash)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <UITableActionCell>
                      <Button size="small" variant="outlined"
                        href={`/instance/${encodeURIComponent(instanceUniqueId)}/sales/orders/${row.OrderId}`}
                        sx={{ textTransform: "none", fontWeight: 700 }}>
                        Open Project
                      </Button>
                    </UITableActionCell>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8, color: "text.secondary" }}>
                  No archived projects found.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{
          px: 2, borderTop: "1px solid", borderColor: "divider", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 52,
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", pl: 1 }}>
            {loading ? "" : visible.length
              ? `Showing ${currentPage * rowsPerPage + 1}–${Math.min((currentPage + 1) * rowsPerPage, visible.length)} of ${visible.length} records`
              : "No results"}
          </Typography>
          <TablePagination
            component="div" count={visible.length} page={currentPage} rowsPerPage={rowsPerPage}
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} sx={{ border: 0 }}
          />
        </Box>
      </UITableShell>
    </Box>
  );
}
